import { uniqBy } from "es-toolkit";
import moment, { utc } from "moment";

const FRIDAY = 5;

// Cache for nextMaturities to avoid recalculation on every call
let cachedMaturities: moment.Moment[] | null = null;
let cacheTimestamp = 0;

export function __clearCacheForTesting() {
  cachedMaturities = null;
  cacheTimestamp = 0;
}

// Configuration interface for maturity generation
export interface MaturityConfig {
  dailyCount: number;
  weeklyCount: number;
  monthlyCount: number;
  quarterlyCount: number;
}

export function isValidExpiration(maturityString: string) {
  const expirationMoment = utc(maturityString, "DDMMMYY")
    .hour(8)
    .minute(0)
    .second(0);

  return nextMaturities().some((maturity) =>
    moment(maturity, "DDMMMYY").isSame(expirationMoment),
  );
}

/**
 * Calculates the time to maturity in years for a given maturity string
 * @param maturity - The maturity date string in DDMMMYY format (e.g., "19JUN24")
 * @returns The time to maturity in years as a decimal number
 */
export function getTTMYears(maturityString: string) {
  const expirationMoment = utc(maturityString, "DDMMMYY")
    .hour(8)
    .minute(0)
    .second(0);
  const now = utc();
  return expirationMoment.diff(now, "seconds") / (365 * 24 * 60 * 60);
}

// Helper function to get the last Friday of a given month
export function getLastFridayOfMonth(monthDate: moment.Moment): moment.Moment {
  const lastDayOfMonth = monthDate
    .clone()
    .endOf("month")
    .startOf("day")
    .add(8, "hours");

  return lastDayOfMonth.day() >= 5 // If last day of month is friday or later
    ? lastDayOfMonth.day(5) // Then use this week friday
    : lastDayOfMonth.day(5 - 7); // Otherwise use last week friday
}

// Helper function to get the next daily expiration from a given date
export function getNextDaily(
  fromDate: moment.Moment,
  isTodayStillValid: boolean,
): moment.Moment {
  if (isTodayStillValid) {
    // If today's 8 AM hasn't passed, return today at 8 AM
    return fromDate.clone().add(8, "hours");
  }

  return fromDate.clone().add(1, "day").add(8, "hours");
}

// Helper function to get the next Friday expiration from a given date
export function getNextWeekly(
  fromDate: moment.Moment,
  isTodayStillValid: boolean,
): moment.Moment {
  const dayOfWeek = fromDate.day();
  let daysUntilFriday: number;

  if (dayOfWeek < FRIDAY || (dayOfWeek === FRIDAY && isTodayStillValid)) {
    // If before Friday 8AM UTC in the current week, get this week's Friday
    daysUntilFriday = FRIDAY - dayOfWeek;
  } else {
    // Get next week's Friday if past 8AM UTC on Friday of this week
    daysUntilFriday = 7 - dayOfWeek + FRIDAY;
  }

  return fromDate.clone().add(daysUntilFriday, "days").add(8, "hours");
}

// Helper function to get the next monthly expiration from a given date
export function getNextMonthly(
  fromDate: moment.Moment,
  isTodayStillValid: boolean,
): moment.Moment {
  const currentMonthLastFriday = getLastFridayOfMonth(fromDate);

  // Check if we should include this month's last Friday
  const beforeCurrentMonthExpiry =
    fromDate.isBefore(currentMonthLastFriday, "day") ||
    (fromDate.isSame(currentMonthLastFriday, "day") && isTodayStillValid);

  if (beforeCurrentMonthExpiry) {
    // Return current month's last Friday
    return currentMonthLastFriday;
  }

  return getLastFridayOfMonth(fromDate.clone().add(1, "month"));
}

/**
 * Internal function that calculates maturity dates without caching.
 */
export function _calculateNextMaturities(): moment.Moment[] {
  const now = utc();
  const today = now.clone().startOf("day");
  const todayAt8AM = today.clone().add(8, "hours");

  // Check if today's 8AM UTC hasn't passed yet
  const isTodayStillValid = now.isSameOrBefore(todayAt8AM);

  // Calculate all expiration types
  const firstDaily = getNextDaily(today, isTodayStillValid);
  const secondDaily = firstDaily.clone().add(1, "day");
  const dailyExpirations = [firstDaily, secondDaily];

  const firstWeekly = getNextWeekly(today, isTodayStillValid);
  const secondWeekly = firstWeekly.clone().add(7, "days");
  const thirdWeekly = firstWeekly.clone().add(14, "days");
  const weeklyExpirations = [firstWeekly, secondWeekly, thirdWeekly];

  const firstMonthly = getNextMonthly(today, isTodayStillValid);
  const secondMonthly = getLastFridayOfMonth(
    firstMonthly.clone().add(1, "month"),
  );
  const monthlyExpirations = [firstMonthly, secondMonthly];

  // Combine all expirations
  const maturities = [
    ...dailyExpirations,
    ...weeklyExpirations,
    ...monthlyExpirations,
  ];

  // Remove duplicates based on date (not time) and sort chronologically
  return uniqBy(maturities, (maturity) => maturity.format("YYYY-MM-DD")).sort(
    (a, b) => a.valueOf() - b.valueOf(),
  );
}

/**
 * Determines if cached value can be used based on 8 AM UTC refresh boundary
 * @param lastRefreshTimestamp - Unix timestamp (milliseconds) when cache was last updated
 * @returns true if cache is valid and can be used, false if recalculation needed
 */
export function canUseCachedValue(lastRefreshTimestamp: number): boolean {
  if (!lastRefreshTimestamp || lastRefreshTimestamp <= 0) {
    return false; // No valid cache exists
  }

  const now = utc();
  const todayAt8AM = now.clone().startOf("day").add(8, "hours");

  let lastRefreshBoundary: moment.Moment;

  if (now.isSameOrBefore(todayAt8AM)) {
    // If it's before or at today's 8 AM, the last refresh boundary was yesterday after 8 AM
    lastRefreshBoundary = todayAt8AM.clone().subtract(1, "day");
  } else {
    // If it's after today's 8 AM, the last refresh boundary was today after 8 AM
    lastRefreshBoundary = todayAt8AM;
  }

  // Convert timestamp to moment for explicit comparison
  const lastRefreshMoment = utc(lastRefreshTimestamp);

  // Cache is valid if it was refreshed at or after the last 8 AM UTC boundary
  return lastRefreshMoment.isAfter(lastRefreshBoundary);
}

/**
 * Generates a list of maturity dates for options instruments.
 *
 * The function calculates several types of maturity dates:
 * - 2 daily options: Either (today and tomorrow) if before 8 AM UTC, or (tomorrow and the day after tomorrow) if after 8 AM UTC
 * - 3 weekly options: The next three Fridays (unless a Friday is already included in daily options)
 * - 2 monthly options: The last Friday of the current month (if before its expiry) and the last Friday of the next month
 *
 * @param config - Optional configuration to override defaults
 * @returns An array of date strings
 */
export function nextMaturities(): string[] {
  if (!cachedMaturities || !canUseCachedValue(cacheTimestamp)) {
    cachedMaturities = _calculateNextMaturities();
    cacheTimestamp = moment().valueOf();
  }

  return cachedMaturities.map((maturity) =>
    maturity.clone().format("DDMMMYY").toUpperCase(),
  );
}
